import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type DietToday as DietTodayT } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";

const DAY_TYPES = [
  { key: "rest", label: "Rest" },
  { key: "light", label: "Light" },
  { key: "moderate", label: "Moderate" },
  { key: "hard", label: "Hard" },
] as const;

export default function DietToday() {
  const [data, setData] = useState<DietTodayT | null | "error">(null);
  const [busy, setBusy] = useState(false);

  function load() {
    api.diet.today().then(setData).catch(() => setData("error"));
  }
  useEffect(load, []);

  const d = data && data !== "error" ? data : null;

  async function setDayType(dt: string) {
    setBusy(true);
    await api.diet.setDayType(dt).catch(() => {});
    load();
    setBusy(false);
  }
  async function toggleMeal(idx: number, logged: boolean) {
    setBusy(true);
    if (logged) await api.diet.clearMeal(idx).catch(() => {});
    else await api.diet.logMeal(idx).catch(() => {});
    load();
    setBusy(false);
  }

  if (data === "error") return <p className="muted">Couldn't load today's plan.</p>;
  if (!d) return <p className="muted">Loading…</p>;

  if (!d.phase || d.meals.length === 0) {
    return (
      <div className="animate-fade-in">
        <h2 className="section-title mb-3">Today's plan</h2>
        <EmptyState
          title="No active phase yet"
          action={
            <Link to="/diet/template" className="btn-accent">
              Set up template
            </Link>
          }
        >
          Confirm your meals and set an active phase on the Template tab, then your daily targets show
          up here.
        </EmptyState>
      </div>
    );
  }

  const t = d.totals;
  return (
    <div className="animate-fade-in">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-title">{d.phase.name}</h2>
        <span className="text-xs text-ink-subtle">today</span>
      </div>

      {/* Day-type selector */}
      <div className="mb-4">
        <span className="field-label">Training day</span>
        <div className="mt-1.5 flex gap-2">
          {DAY_TYPES.map((dt) => (
            <button
              key={dt.key}
              type="button"
              onClick={() => setDayType(dt.key)}
              disabled={busy}
              className={`flex-1 rounded-md border px-2 py-2 text-sm transition duration-fast ${
                d.day_type === dt.key
                  ? "border-accent bg-accent text-white"
                  : "border-ink-line bg-surface text-ink-muted hover:border-ink-subtle/60"
              }`}
            >
              {dt.label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-ink-subtle">Only carbs change with training intensity.</p>
      </div>

      {/* Totals */}
      <div className="card card-pad mb-4">
        <div className="grid grid-cols-4 gap-2 text-center">
          <Macro label="Protein" logged={t.logged.protein_g} target={t.target.protein_g} unit="g" />
          <Macro label="Carbs" logged={t.logged.carbs_g} target={t.target.carbs_g} unit="g" />
          <Macro label="Fat" logged={t.logged.fat_g} target={t.target.fat_g} unit="g" />
          <Macro label="Veg" logged={t.logged.veggie_cups} target={t.target.veggie_cups} unit="cup" />
        </div>
        <div className="mt-2 text-center text-xs text-ink-subtle">target ≈ {t.target.kcal} kcal</div>
      </div>

      {/* Meals */}
      <div className="space-y-2">
        {d.meals.map((m) => {
          const done = m.logged.count > 0;
          return (
            <div key={m.meal_index} className={`card px-4 py-3 ${done ? "border-accent/40" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm text-ink">
                    {m.name}
                    {m.is_shake ? <span className="pill-neutral ml-1.5">shake</span> : null}
                  </div>
                  {m.timing && <div className="text-xs text-ink-subtle">{m.timing}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => toggleMeal(m.meal_index, done)}
                  disabled={busy}
                  className={done ? "btn-secondary px-3 py-1.5 text-xs" : "btn-accent px-3 py-1.5 text-xs"}
                >
                  {done ? "✓ Logged" : "Log as planned"}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-muted tabular-nums">
                <span>P {m.target.protein_g}g</span>
                <span>C {m.target.carbs_g}g</span>
                <span>F {m.target.fat_g}g</span>
                {m.target.veggie_cups > 0 && <span>Veg {m.target.veggie_cups} cup</span>}
                {done && (
                  <span className="text-accent">
                    · logged {m.logged.protein_g}/{m.logged.carbs_g}/{m.logged.fat_g}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-ink-subtle">
        "Log as planned" records the meal at its target macros. Photo/voice logging that maps to your
        plan arrives next.
      </p>
    </div>
  );
}

function Macro({ label, logged, target, unit }: { label: string; logged: number; target: number; unit: string }) {
  const pct = target > 0 ? Math.min(100, Math.round((logged / target) * 100)) : 0;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="font-display text-sm tabular-nums text-ink">
        {logged}
        <span className="text-ink-subtle">/{target}</span>
      </div>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-ink-line/40">
        <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[9px] text-ink-subtle">{unit}</div>
    </div>
  );
}
